import * as React from 'react'

import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { Delegation } from '@/app/types/creator'

const getColumns = (t: (key: string) => string): TableHeaderColumn[] => [
  {
    title: t('creator.delegation-number'),
    key: 'delegationId',
    type: 'string',
    sort: true,
  },
  {
    title: t('creator.reg-number'),
    key: 'vehicleReg',
    type: 'string',
    sort: true,
  },
  { title: t('creator.start'), key: 'start', type: 'date', sort: true },
  { title: t('creator.end'), key: 'end', type: 'date', sort: true },
  { title: t('common.time'), key: 'duration', type: 'duration', sort: true },
  {
    title: t('creator.accommodation'),
    key: 'accommodation',
    type: 'number',
    sort: true,
  },
  { title: t('creator.costs'), key: 'costs', type: 'number', sort: true },
  { title: t('creator.advances'), key: 'advances', type: 'number', sort: true },
  {
    title: t('creator.allowances'),
    key: 'allowances',
    type: 'number',
    sort: true,
  },
  {
    title: t('creator.destination-country'),
    key: 'destinationCountry',
    type: 'string',
    sort: true,
  },
  { title: '', key: null, type: 'string', sort: false },
]

const formatDateCell = (value: string | undefined) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

type Props = {
  delegations: Delegation[]
  driverId: string
  renderRowActions: (delegation: Delegation, driverId: string) => React.ReactNode
}

export const DelegationsDetailsTable = ({
  delegations,
  driverId,
  renderRowActions,
}: Props) => {
  const { t } = useLocalize()
  const columns = React.useMemo(() => getColumns(t), [t])

  const { sortedData, sortConfig, handleHeaderClick } =
    useSortableData<Delegation>({
      data: delegations,
      tableColumns: columns,
      includeActiveSorting: false,
    })

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
      {sortedData.length > 0 ? (
        <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto">
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              sticky
              rounded={false}
              disabledCheckboxToggle
              columns={columns}
              sortConfig={sortConfig}
              onHeaderClick={handleHeaderClick}
            />
            <tbody>
              {sortedData.map((delegation, idx) => (
                <tr
                  key={`${delegation.tripId ?? delegation.delegationId ?? idx}`}
                  className="tw-border-b tw-bg-transparent"
                >
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {String(delegation.delegationId ?? '')}
                  </TableTextRow>
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {String(delegation.vehicleReg ?? '')}
                  </TableTextRow>
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {formatDateCell(delegation.start)}
                  </TableTextRow>
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {formatDateCell(delegation.end)}
                  </TableTextRow>
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {String(delegation.duration ?? '')}
                  </TableTextRow>
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {String(delegation.accommodation ?? '')}
                  </TableTextRow>
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {String(delegation.costs ?? '')}
                  </TableTextRow>
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {String(delegation.advances ?? '')}
                  </TableTextRow>
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {String(delegation.allowances ?? '')}
                  </TableTextRow>
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {String(delegation.destinationCountry ?? '')}
                  </TableTextRow>
                  <TableTextRow size="small" textSize="sm" pointer={false}>
                    {renderRowActions(delegation, driverId)}
                  </TableTextRow>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <TableEmpty title={t('common.no-data')} />
      )}
    </section>
  )
}
